import React, { useContext, useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey } from "@fortawesome/free-solid-svg-icons";
import { DataContext } from "../../../context/DataContext";
import {
  deleteToken,
  deleteUserData,
  getToken,
  getUserByUsername,
} from "../../../api/Account";
import { EstadoError, useAviso } from "../../../components/base";
import moment from "moment";
import { formatearFecha } from "../../../helpers/formato";
import SmallModal from "../../../components/modals/SmallModal";
import ChangePass from "./ChangePass";

// Primera letra del nombre y del apellido; con un solo nombre queda una sola inicial.
const inicialesDe = (user) => {
  const letras = [user.firstName, user.lastName]
    .map((parte) => (parte || "").trim()[0])
    .filter(Boolean)
    .join("")
    .toUpperCase();
  return letras || (user.userName || "").trim().charAt(0).toUpperCase();
};

// Una fecha mínima (año 1) o inválida no se muestra: "—".
const fechaDeRegistro = (fecha) => {
  const m = moment(fecha);
  return fecha && m.isValid() && m.year() >= 1900 ? formatearFecha(fecha) : "";
};

// Fila etiqueta/valor. Si falta el dato, "—"; mientras carga, un esqueleto.
const Fila = ({ etiqueta, valor, cargando }) => (
  <>
    <Typography
      component="dt"
      variant="body2"
      sx={{ color: "text.secondary", fontWeight: 700 }}
    >
      {etiqueta}
    </Typography>
    <Typography
      component="dd"
      variant="body2"
      sx={{ m: 0, color: "text.primary", overflowWrap: "anywhere" }}
    >
      {cargando ? <Skeleton width="70%" /> : valor || "—"}
    </Typography>
  </>
);

const MyAccount = ({ actualizacion }) => {
  const { setIsLogged, setIsLoading, setAccess } = useContext(DataContext);
  const aviso = useAviso();
  const [showModal, setShowModal] = useState(false);
  const [user, setUser] = useState("");
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const token = getToken();

  const getUserData = async () => {
    setIsLoading(true);
    setCargando(true);
    setError(false);
    const result = await getUserByUsername(token);
    if (!result.statusResponse) {
      if (result.error === "eX01") {
        setIsLoading(false);
        deleteUserData();
        deleteToken();
        setIsLogged(false);
        return;
      }

      setIsLoading(false);
      setCargando(false);
      setError(true);
      aviso.error(result.error || "No se pudieron cargar tus datos");
      return;
    }
    setAccess(result.data.userRol.permissions);
    setUser(result.data);
    setIsLoading(false);
    setCargando(false);
  };

  useEffect(() => {
    if (token === null || user === null) {
      setIsLogged(false);
      setIsLoading(false);
      return;
    } else {
      getUserData();
    }
  }, []);

  // Al guardar, el servidor devuelve el usuario actualizado: se refleja aquí sin otra llamada.
  useEffect(() => {
    if (!actualizacion) return;
    setUser((anterior) =>
      anterior
        ? {
            ...anterior,
            firstName: actualizacion.firstName,
            secondName: actualizacion.secondName,
            lastName: actualizacion.lastName,
            secondLastName: actualizacion.secondLastName,
            fullName: actualizacion.fullName,
            phone: actualizacion.phone,
            address: actualizacion.address,
          }
        : anterior
    );
  }, [actualizacion]);

  const nombre = (user.fullName || user.userName || "")
    .trim()
    .replace(/\s+/g, " ");

  return (
    <div>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        {error ? (
          <EstadoError
            titulo="No se pudieron cargar tus datos"
            onReintentar={getUserData}
          />
        ) : (
          <>
            <Stack
              alignItems="center"
              spacing={1}
              sx={{ textAlign: "center", pb: 3 }}
            >
              {cargando ? (
                <>
                  <Skeleton variant="circular" width={96} height={96} />
                  <Skeleton width="60%" height={32} />
                  <Skeleton width="30%" />
                </>
              ) : (
                <>
                  <Avatar
                    sx={{
                      width: 96,
                      height: 96,
                      fontSize: 36,
                      fontWeight: 700,
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                    }}
                  >
                    {inicialesDe(user)}
                  </Avatar>
                  <Typography
                    component="h2"
                    variant="h6"
                    sx={{ fontWeight: 700, overflowWrap: "anywhere" }}
                  >
                    {nombre || "—"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {user.userRol ? user.userRol.rolName : "—"}
                  </Typography>
                </>
              )}
            </Stack>

            <Divider />

            <Box
              component="dl"
              sx={{
                m: 0,
                py: 3,
                display: "grid",
                gridTemplateColumns: "max-content 1fr",
                columnGap: 3,
                rowGap: 1.5,
              }}
            >
              <Fila etiqueta="Usuario" valor={user.userName} cargando={cargando} />
              <Fila etiqueta="Teléfono" valor={user.phone} cargando={cargando} />
              <Fila etiqueta="Dirección" valor={user.address} cargando={cargando} />
              <Fila
                etiqueta="Fecha de registro"
                valor={fechaDeRegistro(user.fechaCreacion)}
                cargando={cargando}
              />
            </Box>

            <Divider />
          </>
        )}

        <Button
          variant="outlined"
          fullWidth
          size="large"
          startIcon={<FontAwesomeIcon icon={faKey} />}
          onClick={() => setShowModal(true)}
          sx={{ mt: 3 }}
        >
          Cambiar contraseña
        </Button>
      </Paper>

      <SmallModal
        isVisible={showModal}
        setVisible={setShowModal}
        titulo="Cambiar Contraseña"
      >
        <ChangePass setShowModal={setShowModal} />
      </SmallModal>
    </div>
  );
};

export default MyAccount;
